/* eslint-disable @typescript-eslint/no-empty-interface */
import * as Device from 'expo-device'
import { ClientOptions } from 'graphql-ws'
import { get, isEmpty } from 'lodash'
// back out of mobx proxies, which aren't supported in React Native
import { configure } from 'mobx'
import { Instance, onSnapshot, SnapshotOut, types } from 'mobx-state-tree'
import { createHttpClient } from 'mst-gql'
import { Platform } from 'react-native'
import branch from 'branch-sdk'
import * as AuthSession from 'expo-auth-session'

import { DdLogs } from '@/modules/datadog'

import { emitter, LogoutEvent } from '../utils/events'
import { extendLogger, LoggerContexts } from '../utils/logger'
import * as Storage from '../utils/storage'
import { EventAnalyticsStore } from './analytics/session-analytics'
import { RootStore as APIRootStore } from './api-store'
import { CommunicationStore } from './communication/communication.store'
import { ENV_PATH_PREFIX, ENV_STORAGE_KEY, EnvironmentStages, EnvironmentStore } from './environment/environment.store'
import { GameStore } from './game/game.store'
import { globalConnectionParams } from './GlobalConnectionParams'
import { LessonsStore } from './lessons'
import { MembershipStore } from './membership/membership.store'
import { MessagesStore } from './messages/messages.store'
import { MondayStore } from './monday/monday.store'
import { NetworkStore } from './network/network.store'
import { SubscriptionClient, WebSocketConnectionState } from './network/SubscriptionClient'
import { PermissionsStore } from './permissions'
import { SessionStore } from './session/session.store'
import { ShareStore } from './share'
import { TelemetryStore } from './telemetry'
import { ZogoStore } from './zogo'

import pkg from '../../../package.json'
import { getBaseUrls } from '@/models/getBaseUrls'
import { PatchedRequestInit } from 'graphql-request/dist/types'
import { auth0Config } from '@/config/auth0'
import { DashboardStore } from './dashboard/dashboard.store'
import { refreshAccessToken } from './session/refreshToken'
import { useSessionStore } from './session/useSessionStore'

configure({ useProxies: 'never', enforceActions: 'never' })

const logger = extendLogger(LoggerContexts.RootStore)

export const isDev = __DEV__ && !Device.isDevice

const isWeb = Platform.OS === 'web'

export let broadcastChannel: BroadcastChannel | null = null
if (isWeb) {
  broadcastChannel = new BroadcastChannel('ppwr_channel')
  broadcastChannel.onmessage = (event) => {
    switch (event.data.type) {
      case 'login':
        {
          if (!useSessionStore.getState().isLoggedIn()) {
            window.location.reload()
          }
        }
        break
      case 'logout':
        {
          if (useSessionStore.getState().isLoggedIn()) {
            window.location.reload()
          }
        }
        break
      default:
        break
    }
  }
}

export const RootStoreModel = types.model('RootStore').props({
  analytics: types.optional(EventAnalyticsStore, {}),
  api: types.optional(APIRootStore, {}),
  communication: types.optional(CommunicationStore, {}),
  environment: types.optional(EnvironmentStore, {}),
  game: types.optional(GameStore, {}),
  network: types.optional(NetworkStore, {}),
  permissions: types.optional(PermissionsStore, {}),
  session: types.optional(SessionStore, {}),
  messages: types.optional(MessagesStore, {}),
  memberships: types.optional(MembershipStore, {}),
  telemetry: types.optional(TelemetryStore, {}),
  share: types.optional(ShareStore, {}),
  zogo: types.optional(ZogoStore, {}),
  monday: types.optional(MondayStore, {}),
  lessons: types.optional(LessonsStore, {}),
  dashboard: types.optional(DashboardStore, {})
})

export interface RootStore extends Instance<typeof RootStoreModel> {}
export interface RootStoreSnapshot extends SnapshotOut<typeof RootStoreModel> {}

export let rootStore: RootStore | undefined
let gqlWsClient: SubscriptionClient
// let legacyGqlClient: LegacySubscriptionClient

//
const rootStoreVersion = '1'
/**
 * The key we'll be saving our state as within async storage.
 */
const disablePersistence = isWeb && window.location.pathname.startsWith('/embedded/')
// reffering source is: window.pa
const ROOT_STATE_STORAGE_KEY = disablePersistence
  ? `root-${window.location.pathname.replaceAll('/', '-')}`
  : `root-${rootStoreVersion}`

/**
 *
 * @param stageFromStorage
 * @returns
 */
async function getPathPrefix(stage: EnvironmentStages) {
  const pathPrefixFromStorage = await Storage.load(ENV_PATH_PREFIX)
  if (typeof pathPrefixFromStorage === 'string' && !!pathPrefixFromStorage) {
    return `${pathPrefixFromStorage}/`
  }

  if (stage === EnvironmentStages.prod) {
    if (Platform.OS === 'web') {
      // for web  we don't need a prefix
      return ''
    }
    return `${pkg.version}/`
  }

  return ''
}

export async function getEnvStage() {
  // load env from storage
  let stageFromStorage = await Storage.load(ENV_STORAGE_KEY)

  const stages: string[] = Object.values(EnvironmentStages)
  const isStageAsString =
    !isEmpty(stageFromStorage) && typeof stageFromStorage === 'string' && stages.includes(stageFromStorage)
  stageFromStorage = isStageAsString ? stageFromStorage : 'dev'
  const EnvStage = process.env.EXPO_PUBLIC_STAGE
  const stageFromEnum = EnvironmentStages[EnvStage as keyof typeof EnvironmentStages]

  const stage: EnvironmentStages = stageFromEnum ? stageFromEnum : EnvironmentStages.dev
  const pathPrefix = await getPathPrefix(stage)
  // const stage: EnvironmentStages = EnvironmentStages.prod
  // const stage: EnvironmentStages = EnvironmentStages.test
  // const stage: EnvironmentStages = EnvironmentStages.dev
  // const stage: EnvironmentStages = EnvironmentStages.local
  // logger.log('process.env', process.env)
  console.log('stageFromStorage', stageFromStorage)
  console.log('stageFromEnum', stageFromEnum)
  console.log('stage', stage)
  console.log('pathPrefix', pathPrefix, !!pathPrefix)
  return { stage, pathPrefix }
}

function initBranch(stage: EnvironmentStages) {
  if (Platform.OS === 'web') {
    //test: key_test_lqijVVYTMPnVxttOt0BzwemlDvgTpxu3

    const branchKey =
      stage === EnvironmentStages.prod
        ? 'key_live_oFckTI3VIRfStxyVy1FxCaliFCpVbtFI'
        : 'key_test_lqijVVYTMPnVxttOt0BzwemlDvgTpxu3'
    branch.init(branchKey, {}, (error, data) => {
      window.analytics.track('Branch Attribution', {
        error,
        data: data?.data_parsed
      })
    })
  }
}

export const getDefaultStore = async () => {
  if (rootStore === undefined) {
    const { stage, pathPrefix } = await getEnvStage()
    initBranch(stage)

    const { baseUrls, baseWebSocketUrls } = getBaseUrls({ pathPrefix })

    const baseURL = baseUrls[stage]
    console.log('LOG: > getDefaultStore > baseURL:', baseURL)
    logger.debug('baseURL', baseURL)
    const baseWebSocketURL = baseWebSocketUrls[stage]
    logger.debug('baseWebSocketURL', baseWebSocketURL)

    const subscriptionClientOptions: ClientOptions = {
      // ClientOptions:
      //   https://github.com/apollographql/subscriptions-transport-ws

      // how long the client should wait in ms for a keep-alive message from the server (default 30000 ms),
      // this parameter is ignored if the server does not send keep-alive messages.
      // This will also be used to calculate the max connection time per connect/reconnect
      // timeout: 5000,

      // object that will be available as first argument of onConnect (in server side), if passed a function
      // - it will call it and send the return value, if function returns as promise
      // - it will wait until it resolves and send the resolved value.
      connectionParams: () => {
        logger.log('connectionParams', globalConnectionParams)
        const { token } = globalConnectionParams
        const headers = token ? { authorization: `bearer ${token}` } : {}
        logger.log('connectionParams headers', headers)
        return { headers }
      },

      on: {
        connecting: () => {
          logger.debug('ClientOptions.on(connecting)')
          rootStore?.network.setWebSocketState(WebSocketConnectionState.connecting)
        },
        connected: (event: unknown) => {
          logger.debug('ClientOptions.on(connected)', event)
          rootStore?.network.setWebSocketState(WebSocketConnectionState.connected)
        },
        closed: (event) => {
          logger.debug('ClientOptions.on(closed)', event)
          DdLogs.error(`Subscription client closed ${JSON.stringify(event)}`)
          // if closed with the `4403: Forbidden` close event
          // the client or the server is communicating that the token
          // is no longer valid and should be therefore refreshed
          // if (event.code === 4403) shouldRefreshToken = true
          rootStore?.network.setWebSocketState(WebSocketConnectionState.closed)
        },
        error: (event) => {
          DdLogs.error(`Subscription client error ${JSON.stringify(event)}`)
          logger.error(`error ${event}`)
        }
      },
      lazy: true,
      // connectionCallback: (errors: Error[], result?: any) => {
      //   if (errors) {
      //     logger.error('connectionCallback error', errors)
      //     return
      //   }
      //   logger.log('connectionCallback result', result)
      // },
      url: baseWebSocketURL
    }

    gqlWsClient = new SubscriptionClient(subscriptionClientOptions)
    // legacyGqlClient = new LegacySubscriptionClient(subscriptionClientOptions)
    const env = {
      baseURL,
      baseWebSocketURL,
      kinesisGameEventCommandsUrl: `${baseURL}/kinesis/game-events`,
      stage,
      // if we don't have http mst-gql will only use WebSockets
      gqlHttpClient: createHttpClient(`${baseURL}`, {
        // refreshToken
        fetch: async (url: string, init: PatchedRequestInit) => {
          if (!rootStore) {
            throw new Error("Rootstore isn't ready")
          }
          const tokenConfigSnapshot = useSessionStore.getState().tokenConfig

          if (!tokenConfigSnapshot) {
            console.warn('No token config')
          }

          let accessToken = tokenConfigSnapshot?.accessToken ?? ''

          if (tokenConfigSnapshot && useSessionStore.getState().shouldRefresh()) {
            const newTokenConfig = await refreshAccessToken(
              JSON.parse(JSON.stringify(tokenConfigSnapshot)) as AuthSession.TokenResponseConfig,
              rootStore
            )
            accessToken = newTokenConfig.accessToken
          }

          try {
            const response = await fetch(url, {
              ...init,
              // @ts-ignore
              headers: {
                ...init?.headers,
                authorization: `Bearer ${accessToken}`
              }
            })

            // Clone response to safely read body without consuming it
            const clonedResponse = response.clone()
            let json: any

            try {
              json = await clonedResponse.json()
            } catch (e) {
              // Handle cases where response is not JSON
              json = {}
            }

            // Extract status code from JSON errors or fallback to response status
            const statusCode = json?.errors?.[0]?.extensions?.originalError?.statusCode ?? response.status
            // We only logout if the 401 or 403 error is from one of our api endpoints
            if (statusCode === 401 || statusCode === 403) {
              logger.debug(`Fetch intercepted: ${statusCode} error`)
              emitter.emit(LogoutEvent)
              rootStore?.session.logout()
            }

            // Return a response with the parsed JSON
            return new Response(JSON.stringify(json), {
              status: response.status,
              statusText: response.statusText,
              headers: response.headers
            })
          } catch (error) {
            logger.error('Fetch error', error, url, init)
            throw error // Rethrow to maintain error propagation
          }
        }
      }),
      //   const customFetch = (uri, options) => {
      //   const { header } = Hawk.client.header(
      //     "http://example.com:8000/resource/1?b=1&a=2",
      //     "POST",
      //     { credentials: credentials, ext: "some-app-data" }
      //   );
      //   options.headers.Authorization = header;
      //   return fetch(uri, options);
      // };

      // const link = new HttpLink({ fetch: customFetch });
      // gqlHttpClient: createHttpClient(`${baseURL}/graphql`, { fetch: customFetch }),
      // @NOTE: This is an Anthony hack to make GraphQL subscriptions work locally until NestJs 9.0 is released.
      // @TODO: One day NestJs + Apollo 3.0 + Graphql-Ws will come to life and we can consolidate this to the latest graphql-ws
      // gqlWsClient: legacyGqlClient,
      graphqlWsClient: gqlWsClient,
      socket: WebSocket
    }

    // add middleware to inject session
    // env.gqlWsClient.use([
    //   {
    //     applyMiddleware(opts, next) {
    //       logger.log('applyMiddleware opts', opts)
    //       // opts.context = {
    //       //   token: __super_secret_token__,
    //       // }
    //       next()
    //     },
    //   },
    // ])
    try {
      // load data from storage
      const data = await Storage.load(ROOT_STATE_STORAGE_KEY)
      // logger.log('loaded data from storage', data, RootStoreModel.is(data))
      if (RootStoreModel.is(data) && !disablePersistence) {
        rootStore = RootStoreModel.create(
          {
            ...data
          },
          env
        )
      } else {
        logger.warn('unable to load storage!')
        rootStore = RootStoreModel.create({}, env)
      }
    } catch (error) {
      // if there's any problems loading, then let's at least fallback to an empty state
      // instead of crashing.
      rootStore = RootStoreModel.create({}, env)
      // but please inform us what happened
      logger.error('unable to load storage from cache', { error })
    }
    if (rootStore && !disablePersistence) {
      logger.debug('setup set stage', rootStore.environment.setStage(stage))
      // track changes & save to storage
      logger.debug('setup onSnapshot', !!rootStore)
      onSnapshot(rootStore, (snapshot) => {
        // logger.log('snap! onSnapshot')
        Storage.save(ROOT_STATE_STORAGE_KEY, snapshot)
      })
    }
  }

  return rootStore
}
